import { useRef, useState } from 'react';
import { useAppSelector } from '../../../../redux/hooks';
import { useOnFarmActionBlocked } from '../../../../hooks/useOnFarmActionBlocked';
import BulkDepositImportPreview from './BulkDepositImportPreview';
import { downloadOnFarmBulkDepositTemplate, uploadOnFarmBulkDeposit } from '../../../../api/onFarmTransactionApi';
import { showToast } from '../../../../services/toast.service';
import { parseExcelFile, ParsedExcelWorkbook, revokeParsedExcelLogos } from '../../../../utils/parseExcelFile';
import {
  BulkDepositValidationError,
  getBulkDepositValidationMessage,
  parseBulkDepositValidationErrors,
} from '../../../../utils/parseBulkDepositValidation';
import { PromiseError, toastMessages } from '../../../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../../../constants/common';
const EXCEL_ACCEPT = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

const getDownloadFileName = (contentDisposition?: string) => {
  const fallbackName = `${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_BulkDeposit_Template.xlsx`;
  if (!contentDisposition) {
    return fallbackName;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  return fileNameMatch?.[1] ?? fallbackName;
};

const OnFarmBulkDeposit = (): JSX.Element => {
  const activeAccount = useAppSelector(state => state.activeAccount);
  const isOnFarmLocked = useOnFarmActionBlocked();
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [parsedWorkbook, setParsedWorkbook] = useState<ParsedExcelWorkbook | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [validationErrors, setValidationErrors] = useState<BulkDepositValidationError[]>([]);
  const [validationMessage, setValidationMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetBulkImportState = () => {
    revokeParsedExcelLogos(parsedWorkbook);
    setPreviewModalOpen(false);
    setImportFile(null);
    setParsedWorkbook(null);
    setIsImporting(false);
    setValidationErrors([]);
    setValidationMessage('');
  };

  const handleDownloadTemplate = async () => {
    if (isDownloadingTemplate) {
      return;
    }

    try {
      setIsDownloadingTemplate(true);
      const response = await downloadOnFarmBulkDepositTemplate();
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getDownloadFileName(response.headers['content-disposition']));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success(toastMessages.TEMPLATE_DOWNLOAD_SUCCESSFUL);
    } catch {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleImportExcel = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      const workbookData = await parseExcelFile(file);
      revokeParsedExcelLogos(parsedWorkbook);
      setImportFile(file);
      setParsedWorkbook(workbookData);
      setValidationErrors([]);
      setValidationMessage('');
      setPreviewModalOpen(true);
    } catch {
      showToast.error(toastMessages.FAILED_TO_PARSE_EXCEL);
    }
  };

  const handleConfirmImport = async () => {
    if (!importFile || !activeAccount?.accountId) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return;
    }

    try {
      setIsImporting(true);
      setValidationErrors([]);
      setValidationMessage('');
      await uploadOnFarmBulkDeposit(importFile, Number(activeAccount.accountId));
      showToast.success(toastMessages.BULK_UPLOAD_SUCCESSFUL);
      resetBulkImportState();
    } catch (error: any) {
      if (error.code === PromiseError.NETWORK) {
        showToast.error(toastMessages.NETWORK);
      } else {
        const parsedErrors = parseBulkDepositValidationErrors(error);
        const message = getBulkDepositValidationMessage(error);

        setValidationErrors(parsedErrors);
        setValidationMessage(message);

        if (parsedErrors.length > 0) {
          showToast.error(toastMessages.BULK_UPLOAD_VALIDATION_FAILED);
        } else {
          showToast.error(message || toastMessages.SOMETHING_WENT_WRONG);
        }
      }
      setIsImporting(false);
    }
  };

  return (
    <>
      <div className="bulk-deposit-section">
        <div className="bulk-deposit-instructions">
          <h3>{LABEL_CONSTANTS.BULK_DEPOSIT_INSTRUCTIONS_TITLE}</h3>
          <ol>
            <li>{LABEL_CONSTANTS.BULK_DEPOSIT_INSTRUCTION_DOWNLOAD}</li>
            <li>{LABEL_CONSTANTS.BULK_DEPOSIT_INSTRUCTION_FILL}</li>
            <li>{LABEL_CONSTANTS.BULK_DEPOSIT_INSTRUCTION_IMPORT}</li>
          </ol>
        </div>
        <div className="bulk-deposit-actions">
          <button
            type="button"
            className="button medium blue inverted"
            onClick={handleDownloadTemplate}
            disabled={isOnFarmLocked || isDownloadingTemplate}>
            {isDownloadingTemplate ? 'Downloading…' : BUTTON_CONSTANTS.DOWNLOAD_TEMPLATE}
          </button>
          <button
            type="button"
            className="button medium blue"
            onClick={handleImportExcel}
            disabled={isOnFarmLocked}>
            {BUTTON_CONSTANTS.IMPORT_EXCEL}
          </button>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={EXCEL_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        data-testid="bulk-deposit-file-input"
      />
      <BulkDepositImportPreview
        isOpen={previewModalOpen}
        fileName={importFile?.name ?? ''}
        workbook={parsedWorkbook}
        isImporting={isImporting}
        validationErrors={validationErrors}
        validationMessage={validationMessage}
        onClose={resetBulkImportState}
        onConfirmImport={handleConfirmImport}
      />
    </>
  );
};

export default OnFarmBulkDeposit;
